import type { ComponentProps } from 'react';
import React, { useId } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { borderTop, boxShadow, color, display, justifyContent, textAlign } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { Grid, GridItem } from 'common/components/Grid';
import Separator from 'common/components/Separator';
import { UspCtaLink } from './UspCtaLink';
import { UspHeader } from './UspHeader';
import { UspItem } from './UspItem';

interface CommonUspTeaserProps extends ComponentProps<typeof UspHeader> {
  items: Array<Omit<ComponentProps<typeof UspItem>, 'isCompactWidth'>>;
  isContentWithReducedWidth: boolean;
  ctaLinkLabel?: Nullable<string>;
  ctaLinkHref?: Nullable<string>;
  backgroundColor?: string;
  textColor?: string;
  boxShadow?: string;
}

const UnstyledUspTeaserGrid = styled(Grid)<any>`
  ${color};
  ${boxShadow};
  ${justifyContent};
  ${textAlign};
  ${display};
  ${borderTop};
`;

const UspTeaserGrid = (props: ComponentProps<typeof UnstyledUspTeaserGrid>) => (
  <UnstyledUspTeaserGrid
    textAlign="center"
    bg="sys.neutral.background.weak"
    color="sys.neutral.text.strong"
    boxShadow={['0 2px 6px 0 rgba(192, 192, 192, 0.5)', 'none']}
    display="flex"
    justifyContent="center"
    px={['sp_16', null, 'sp_36', 'sp_48']}
    {...props}
  />
);

const UnstyledUspTeaserInnerWrapper = styled(GridItem)``;

const UspTeaserInnerWrapper = (props: ComponentProps<typeof UnstyledUspTeaserInnerWrapper>) => (
  <UnstyledUspTeaserInnerWrapper px={0} {...props} />
);

const UnstyledUspLayout = styled(Flex)``;

const UspLayout = (props: ComponentProps<typeof UnstyledUspLayout>) => (
  <UnstyledUspLayout
    flexDirection={['column', 'row']}
    alignItems={['center', 'flex-start']}
    justifyContent="center"
    flexWrap="wrap"
    {...props}
  />
);

const CommonUspTeaser = ({
  title,
  subtitleHtml,
  items,
  ctaLinkLabel,
  ctaLinkHref,
  backgroundColor,
  textColor,
  isContentWithReducedWidth,
  boxShadow: bs,
}: CommonUspTeaserProps) => {
  const headingId = useId();

  const isItemCountDivisibleByThree = items.length % 3 === 0;
  const hasCta = ctaLinkLabel && ctaLinkHref;

  return (
    <>
      {backgroundColor && <Separator />}
      <UspTeaserGrid
        data-testid="usp-teaser-container"
        {...(backgroundColor && { bg: backgroundColor })}
        color={textColor}
        boxShadow={bs}
        data-corgi-id="common-usp-teaser"
        as="section"
        aria-labelledby={headingId}
      >
        <UspHeader id={headingId} title={title} subtitleHtml={subtitleHtml} />
        <UspTeaserInnerWrapper
          data-testid="usp-teaser-inner-wrapper"
          {...(isContentWithReducedWidth ? { width: [1, 6 / 8, 8 / 12] } : {})}
        >
          <UspLayout>
            {items.map((props, index) => (
              <UspItem {...props} key={index} isCompactWidth={isItemCountDivisibleByThree} />
            ))}
          </UspLayout>
        </UspTeaserInnerWrapper>
        {hasCta && <UspCtaLink href={ctaLinkHref} label={ctaLinkLabel} />}
      </UspTeaserGrid>
    </>
  );
};

export default CommonUspTeaser;
